const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface AuthToken {
  access_token: string;
  token_type: string;
}

export interface User {
  username: string;
  email?: string;
  full_name?: string;
  disabled?: boolean;
  role?: string;
}

class AuthService {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('access_token');
  }

  async login(credentials: LoginCredentials): Promise<AuthToken> {
    const formData = new FormData();
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 504) {
        throw new Error('Server timeout - please try again in a moment');
      }
      throw new Error('Invalid credentials');
    }

    const tokenData: AuthToken = await response.json();
    this.token = tokenData.access_token;
    localStorage.setItem('access_token', this.token);

    return tokenData;
  }

  logout(): void {
    this.token = null;
    localStorage.removeItem('access_token');
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return this.token !== null;
  }

  async getCurrentUser(): Promise<User> {
    if (!this.token) {
      throw new Error('No token available');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.logout(); // Token expired or invalid
      }
      throw new Error('Failed to get user info');
    }

    return response.json();
  }

  getAuthHeaders(): Record<string, string> {
    if (!this.token) {
      return {};
    }
    
    return {
      'Authorization': `Bearer ${this.token}`,
    };
  }
}

export const authService = new AuthService();